// AI-CONTEXT: See docs/features/planner-overzicht-bkb.md
// Labels op een huishouden (verzoek BuurtKlusBedrijf 2026-10-06): vlaggen
// náást de status, meerdere tegelijk, per organisatie in te richten
// (Instellingen → Ondersteuning → 'Labels bij huishoudens'). Twee labels zijn
// ingebouwd omdat de app ze zelf kent: 'Opnieuw inplannen' zet een huishouden
// in de lijst 'Inplannen' (planningQueue) en gaat vanzelf weg zodra er een
// bezoek is ingepland (clearReplanLabel); 'Extra aandacht' is er zodat niet
// elke organisatie dat eerst zelf hoeft aan te maken.
//
// Opslag: `CompagnyMeta.AdressLabels` is een map per sleutel
// `{ Label, Color, Hidden, Position }`, net als AdressStatusConfig, zodat een
// veld met een dotted path te schrijven is. Het huishouden draagt de SLEUTELS
// in `Adresses.Labels: string[]`; hernoemen raakt dus geen huishouden.

import { v4 as uuid } from 'uuid'
import { PALETTE_COLORS } from '../colorPalette'

/** De sleutel die de lijst 'Inplannen' kent. Niet te verbergen. */
const REPLAN_LABEL_KEY = 'replan'

/** Ingebouwd gemak; mag een organisatie wél verbergen. */
const ATTENTION_LABEL_KEY = 'attention'

/**
 * Vaste kleurnamen, elk een CSS-klasse (`label-chip--<naam>`): geen hexcodes
 * in de gegevens en geen inline stijl in de rij.
 * @type {Array<string>}
 */
// Hetzelfde palet als de statuskleur in de agenda: één bron, één set CSS-klassen.
const LABEL_COLORS = PALETTE_COLORS

/** @type {Array<{key: string, label: string, color: string}>} */
const BUILT_IN_LABELS = [
    { key: REPLAN_LABEL_KEY, label: 'Opnieuw inplannen', color: 'coral' },
    { key: ATTENTION_LABEL_KEY, label: 'Extra aandacht', color: 'teal' },
]

/**
 * Aan/uit-vlaggen komen in twee vormen voor: ToggleButton schrijft tekst.
 * @param {*} value
 * @returns {boolean}
 */
const aan = (value) => value === true || value === 'true'

/**
 * @typedef {object} AdressLabel
 * @property {string} key - De sleutel zoals hij op het huishouden staat
 * @property {string} label - De naam die de gebruiker leest
 * @property {string} color - Een naam uit LABEL_COLORS
 * @property {boolean} builtIn - Ingebouwd (sleutel ligt vast) of eigen
 */

/**
 * De labels van een organisatie, zichtbaar en op volgorde: eerst de ingebouwde,
 * dan de eigen op `Position`.
 *
 * @param {object} [meta] - Het CompagnyMeta-document (of alleen `AdressLabels`)
 * @param {object} [opties]
 * @param {boolean} [opties.includeHidden=false] - Ook de verborgen labels, met `hidden: true` (instellingenscherm)
 * @param {string} [opties.field='AdressLabels'] - Het veld op CompagnyMeta
 * @param {Array<{key: string, label: string, color: string}>} [opties.builtIns=BUILT_IN_LABELS]
 * @returns {Array<AdressLabel & {hidden?: boolean}>}
 */
const resolveAdressLabels = (meta, { includeHidden = false, field = 'AdressLabels', builtIns = BUILT_IN_LABELS } = {}) => {
    const config = (meta && meta[field] && typeof meta[field] === 'object' && !Array.isArray(meta[field]))
        ? meta[field]
        : {}

    const naam = (eigen, standaard) => {
        const tekst = typeof eigen === 'string' ? eigen.trim() : ''
        return tekst || standaard
    }
    const kleur = (eigen, standaard) => (LABEL_COLORS.includes(eigen) ? eigen : standaard)

    // 'Opnieuw inplannen' stuurt de lijst 'Inplannen' en hoort daarom altijd te bestaan.
    const isReplan = (key) => key === REPLAN_LABEL_KEY && builtIns.some((l) => l.key === key)
    const verborgen = (key) => !isReplan(key) && aan(config[key]?.Hidden)

    const ingebouwd = builtIns
        .filter((l) => includeHidden || !verborgen(l.key))
        .map((l) => ({
            key: l.key,
            label: naam(config[l.key]?.Label, l.label),
            color: kleur(config[l.key]?.Color, l.color),
            builtIn: true,
            ...(includeHidden ? { hidden: verborgen(l.key) } : {}),
        }))

    const eigen = Object.keys(config)
        .filter((key) => !builtIns.some((l) => l.key === key))
        .map((key) => ({ key, ...config[key] }))
        .filter((l) => (includeHidden || !aan(l.Hidden)) && typeof l.Label === 'string' && l.Label.trim() !== '')
        .sort((a, b) => (Number(a.Position) || 0) - (Number(b.Position) || 0))
        .map((l) => ({
            key: l.key,
            label: l.Label.trim(),
            color: kleur(l.Color, 'grey'),
            builtIn: false,
            ...(includeHidden ? { hidden: aan(l.Hidden) } : {}),
        }))

    return [...ingebouwd, ...eigen]
}

/**
 * @param {{Labels?: *}} [item] - Het huishouden
 * @returns {Array<string>} De labelsleutels; een ontbrekend of verkeerd veld leest als geen
 */
const labelsOf = (item) => (Array.isArray(item?.Labels) ? item.Labels.filter((k) => typeof k === 'string') : [])

/**
 * @param {object} item
 * @param {string} key
 * @returns {boolean}
 */
const hasLabel = (item, key) => labelsOf(item).includes(key)

/**
 * De nieuwe `Labels`-lijst na het aan- of uitzetten van één label. Nooit
 * dubbel, en de andere labels blijven staan zoals ze stonden.
 * @param {object} item - Het huishouden zoals het nu is
 * @param {string} key
 * @param {boolean} on
 * @returns {{Labels: Array<string>}}
 */
const labelPatch = (item, key, on) => {
    const huidig = labelsOf(item)
    const zonder = huidig.filter((k) => k !== key)
    return { Labels: on ? [...zonder, key] : zonder }
}

/**
 * De labels die een kijker op een project in het portfolio-overzicht kan
 * zetten (Instellingen → Portfolio → 'Labels bij projecten'). Zelfde vorm als
 * bij huishoudens, eigen veld, en geen ingebouwde labels: 'Opnieuw inplannen'
 * betekent iets in de planning van huishoudens, niet bij een project.
 * AI-CONTEXT: See src/helpers/portfolio/projectMarks.js
 * @param {object} [meta] - Het CompagnyMeta-document van de kijker
 * @param {{includeHidden?: boolean}} [opties]
 * @returns {Array<AdressLabel & {hidden?: boolean}>}
 */
const resolveProjectLabels = (meta, { includeHidden = false } = {}) =>
    resolveAdressLabels(meta, { includeHidden, field: 'ProjectLabels', builtIns: [] })

/** @returns {string} Een sleutel voor een eigen label van de organisatie */
const newCustomLabelKey = () => `custom-${uuid()}`

export { resolveProjectLabels, REPLAN_LABEL_KEY, ATTENTION_LABEL_KEY, LABEL_COLORS, BUILT_IN_LABELS, labelsOf, hasLabel, labelPatch, newCustomLabelKey }
export default resolveAdressLabels
